import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (loginError) setLoginError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');
    const result = await login(formData.email, formData.password);
    if (result.success) {
      toast.success('Login successful!');
      navigate('/dashboard');
    } else {
      setLoginError(result.error || 'Incorrect email or password');
      toast.error(result.error || 'Incorrect email or password');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-1">
        {/* Left side - Form */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
          <div className="max-w-md w-full space-y-8">
            {/* Logo & Motto with animation */}
            <div className="text-center lg:text-left animate-login-fade-in">
              <div
                className="h-24 w-24 mx-auto lg:mx-0 mb-3 rounded-2xl overflow-hidden bg-pfap-dark p-2 shadow-lg"
                data-testid="login-logo-container"
              >
                <img
                  src="/pfap-logo-academy-black.png"
                  alt="PFAP Academy"
                  className="h-full w-full object-contain"
                  data-testid="login-logo"
                />
              </div>
              <p
                className="text-sm font-semibold text-pfap-green dark:text-pfap-sage tracking-widest uppercase font-body mb-5 lg:text-left text-center"
                data-testid="login-motto"
              >
                Where football meets education
              </p>
              <h2
                className="text-3xl sm:text-4xl font-heading uppercase text-pfap-green dark:text-gray-100 animate-login-slide-down"
                data-testid="login-heading"
              >
                Welcome Back
              </h2>
              <p className="mt-2 text-base text-gray-600 dark:text-gray-400 font-body">
                Sign in to PFAP Hub
              </p>
            </div>

            {/* Login Form */}
            <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-testid="login-form">
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-gray-700 dark:text-gray-300 font-body text-sm">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 transition-colors duration-200 hover:border-pfap-green focus:border-pfap-green focus:ring-pfap-green/30"
                    placeholder="you@example.com"
                    data-testid="login-email-input"
                  />
                </div>

                {/* Password with toggle */}
                <div>
                  <Label htmlFor="password" className="text-gray-700 dark:text-gray-300 font-body text-sm">
                    Password
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 pr-10 transition-colors duration-200 hover:border-pfap-green focus:border-pfap-green focus:ring-pfap-green/30"
                      placeholder="Enter your password"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pfap-green dark:hover:text-pfap-sage transition-colors duration-150"
                      tabIndex={-1}
                      data-testid="login-password-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Remember Me & Forgot Password row */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="login-remember-me">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-pfap-green focus:ring-pfap-green/40 accent-pfap-green cursor-pointer"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400 font-body">Remember me</span>
                </label>
                <Link
                  to="/change-password"
                  className="text-sm font-medium text-pfap-blue hover:text-pfap-blue/80 dark:text-pfap-blue dark:hover:text-pfap-blue/80 transition-colors duration-150 font-body"
                  data-testid="login-forgot-password"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Inline Error Message */}
              {loginError && (
                <div
                  className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 animate-login-fade-in"
                  data-testid="login-error-message"
                  role="alert"
                >
                  <svg className="h-4 w-4 text-red-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-red-700 dark:text-red-300 font-body">{loginError}</span>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-pfap-green hover:bg-pfap-green/90 dark:bg-pfap-green dark:hover:bg-pfap-green/80 text-white font-body font-medium h-11 text-base transition-all duration-200 hover:shadow-lg"
                disabled={isLoading}
                data-testid="login-submit-button"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>

              <p className="text-center text-sm text-gray-600 dark:text-gray-400 font-body">
                Don&apos;t have an account?{' '}
                <Link
                  to="/register"
                  className="font-medium text-pfap-blue hover:text-pfap-blue/80 dark:text-pfap-blue dark:hover:text-pfap-blue/80 transition-colors duration-150"
                  data-testid="login-register-link"
                >
                  Register here
                </Link>
              </p>
            </form>
          </div>
        </div>

        {/* Right side - Branding */}
        <div className="hidden lg:flex flex-1 bg-pfap-green items-center justify-center p-12 relative overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
          <div className="max-w-sm text-center relative z-10 animate-login-slide-down">
            <img
              src="/pfap-logo-academy-black.png"
              alt="PFAP Academy"
              className="h-36 mx-auto mb-8 drop-shadow-2xl rounded-2xl"
              data-testid="login-branding-logo"
            />
            <h1 className="text-4xl font-heading uppercase text-white mb-3 tracking-wide">
              PFAP Hub
            </h1>
            <p className="text-lg text-pfap-sage leading-relaxed font-body">
              Pro-Football Academy Portugal
            </p>
            <div className="mt-5 flex items-center justify-center gap-3">
              <span className="h-px w-8 bg-pfap-sage/40" />
              <p className="text-white/80 text-sm font-body italic tracking-wide">
                Where football meets education
              </p>
              <span className="h-px w-8 bg-pfap-sage/40" />
            </div>
            <p className="mt-8 text-pfap-sage/50 text-xs font-heading uppercase tracking-[0.2em]">
              Fulfill Your Dreams
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer
        className="text-center py-3 text-xs text-gray-400 dark:text-gray-500 font-body bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700"
        data-testid="login-footer"
      >
        &copy; 2026 PFAP Hub. All rights reserved.
      </footer>

      {/* Inline CSS for animations */}
      <style>{`
        @keyframes loginFadeIn {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes loginSlideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-login-fade-in {
          animation: loginFadeIn 0.7s ease-out both;
        }
        .animate-login-slide-down {
          animation: loginSlideDown 0.8s ease-out 0.15s both;
        }
      `}</style>
    </div>
  );
};

export default Login;
